import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  useChangeEvChargerScheduleStateMutation,
  useChargingSessionStartMutation,
  useChargingSessionStopMutation,
  useDeleteEvChargerScheduleMutation,
  useRemoveEvChargerMutation,
  useViewEvChargerQuery,
  useViewEvChargerScheduleListQuery,
  useViewEvChargerSessionGraphQuery,
  useViewEvChargerSessionListQuery,
} from "../../../../redux/api/ev-charger/evChargerAPI";
import BillingSummaryCard from "../../../../shared/components/billing-summary-card/billing-summary-card";
import ChargeHistoryCard from "../../../../shared/components/charge-history-card/charge-history-card";
import EvChargerGauge from "../../../../shared/components/ev-charger-gauge/ev-charger-gauge";
import { EEvChargerStatus } from "../../../../shared/oversight-core/enums/charger-status";
import { EEvGraphViewType } from "../../../../shared/oversight-core/enums/ev-graph-view-type";
import { IChargingScheduleItem } from "../../../../shared/oversight-core/interfaces/charging-schedule-item";
import { IHttpError } from "../../../../shared/oversight-core/interfaces/http-errror";
import ActiveInactiveIndicator from "../../../../shared/oversight-core/ui-elements/active-inactive-indicator/active-inactive-indicator";
import AppDropDown from "../../../../shared/oversight-core/ui-elements/app-dropdown/app-drop-down";
import AppTabs from "../../../../shared/oversight-core/ui-elements/app-tabs/app-tabs";
import AppToggle from "../../../../shared/oversight-core/ui-elements/app-toggle/app-toggle";
import BackArrowButton from "../../../../shared/oversight-core/ui-elements/back-arrow-button/back-arrow-button";
import BarChart from "../../../../shared/oversight-core/ui-elements/bar-chart/bar-chart";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import DataTable from "../../../../shared/oversight-core/ui-elements/data-table/data-table";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Pagination from "../../../../shared/oversight-core/ui-elements/pagination/pagination";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  calculateDateRange,
  formatGraphPointLabel,
  getGraphDateDisplay,
  getViewType,
  getXAxisLabel,
} from "../../../../shared/oversight-core/utils/date-utils";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../shared/oversight-core/utils/toast";
import translate from "../../../../shared/oversight-core/utils/translate";
import AddUpdateDeviceModal from "../components/modals/add-update-device-modal/add-update-device-modal";
import AddUpdateScheduleModal from "../components/modals/add-update-schedule-modal/add-update-schedule-modal";
import RemoveDeviceModal from "../components/modals/remove-device-modal/remove-device-modal";
import styles from "./view-device.module.scss";

const ViewDevice = () => {
  const navigate = useNavigate();
  const { id: evChargerId = "" } = useParams<{ id: string }>();
  const { setPageTitle } =
    useOutletContext<{
      setPageTitle?: (title?: string) => void;
    }>() ?? {};

  const [showAddUpdateScheduleModal, setShowAddUpdateScheduleModal] =
    useState(false);
  const [showEditDeviceModal, setShowEditDeviceModal] = useState(false);
  const [showRemoveDeviceModal, setShowRemoveDeviceModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState<"history" | "schedule">(
    "history",
  );
  const [selectedChartTabId, setSelectedChartTabId] = useState<number>(2);
  const [selectedChartDate, setSelectedChartDate] = useState(new Date());
  const [historyCurrentPage, setHistoryCurrentPage] = useState<number>(1);
  const historyItemsPerPage = 3;

  const chartViewType = getViewType(selectedChartTabId);
  const { startDate: chartStartDate, endDate: chartEndDate } =
    calculateDateRange(selectedChartDate, chartViewType);

  const { data: chartGraphPoints = [], isFetching: isFetchingChartGraph } =
    useViewEvChargerSessionGraphQuery(
      {
        evChargerId: evChargerId || "",
        startDate: chartStartDate,
        endDate: chartEndDate,
        viewType: chartViewType,
        zoneOffset: "Asia/Colombo",
      },
      { skip: !evChargerId },
    );

  const chartLabels =
    chartGraphPoints.length > 0
      ? chartGraphPoints.map((point) =>
          formatGraphPointLabel(point.periodStart, chartViewType),
        )
      : [];

  const chartData =
    chartGraphPoints.length > 0
      ? chartGraphPoints.map((point) => point.totalEnergyConsumed ?? 0)
      : [];

  const handlePrevChartDate = () => {
    if (chartViewType === EEvGraphViewType.HOUR) {
      setSelectedChartDate((prev) => moment(prev).subtract(1, "day").toDate());
    } else if (chartViewType === EEvGraphViewType.DAY) {
      setSelectedChartDate((prev) =>
        moment(prev).subtract(1, "month").toDate(),
      );
    } else if (chartViewType === EEvGraphViewType.MONTH) {
      setSelectedChartDate((prev) => moment(prev).subtract(1, "year").toDate());
    }
  };

  const handleNextChartDate = () => {
    if (chartViewType === EEvGraphViewType.HOUR) {
      setSelectedChartDate((prev) => moment(prev).add(1, "day").toDate());
    } else if (chartViewType === EEvGraphViewType.DAY) {
      setSelectedChartDate((prev) => moment(prev).add(1, "month").toDate());
    } else if (chartViewType === EEvGraphViewType.MONTH) {
      setSelectedChartDate((prev) => moment(prev).add(1, "year").toDate());
    }
  };

  const {
    data: viewData,
    isFetching: isFetchingEvCharger,
    refetch: refetchEvCharger,
  } = useViewEvChargerQuery(
    { evChargerId: evChargerId || "" },
    { skip: !evChargerId },
  );

  useEffect(() => {
    const deviceName = viewData?.evCharger?.name?.trim();
    if (deviceName) {
      setPageTitle?.(`Device > ${deviceName}`);
    }

    return () => {
      setPageTitle?.(undefined);
    };
  }, [setPageTitle, viewData?.evCharger?.name]);

  const { data: latestSessionResponse } = useViewEvChargerSessionListQuery(
    {
      evChargerId: evChargerId || "",
      pageNumber: 0,
      pageSize: 1,
    },
    { skip: !evChargerId },
  );
  const { data: sessionListResponse, isFetching: isFetchingSessionList } =
    useViewEvChargerSessionListQuery(
      {
        evChargerId: evChargerId || "",
        pageNumber: historyCurrentPage - 1,
        pageSize: historyItemsPerPage,
      },
      { skip: !evChargerId },
    );
  const {
    data: scheduleListResponse,
    isFetching: isFetchingScheduleList,
    refetch: refetchScheduleList,
  } = useViewEvChargerScheduleListQuery(
    { evChargerId: evChargerId || "" },
    { skip: true },
  );
  const [changeScheduleState, { isLoading: isChangingScheduleState }] =
    useChangeEvChargerScheduleStateMutation();
  const [deleteSchedule, { isLoading: isDeletingSchedule }] =
    useDeleteEvChargerScheduleMutation();
  const [removeEvCharger, { isLoading: isRemovingEvCharger }] =
    useRemoveEvChargerMutation();
  const [chargingSessionStart, { isLoading: isLoadingSessionStart }] =
    useChargingSessionStartMutation();
  const [chargingSessionStop, { isLoading: isLoadingSessionStop }] =
    useChargingSessionStopMutation();

  const firstSession = latestSessionResponse?.sessions.elements?.[0];
  const lastChargeDate = firstSession?.startTime
    ? moment(firstSession.startTime).format("MMM DD")
    : "-";
  const lastChargePercent = firstSession?.currentSocPercent ?? "";
  const chargers = latestSessionResponse?.sessions.totalElements ?? 0;
  const totalChartUsage = sessionListResponse?.stats?.totalEnergyConsumed ?? 0;
  const totalUsers = sessionListResponse?.stats?.uniqueRfidCount ?? 0;

  const handleToggleSchedule = (id: string, val: boolean) => {
    const status = val ? "ACTIVE" : "PAUSED";
    changeScheduleState({ scheduleId: id, status })
      .unwrap()
      .then(() => {
        showSuccessMessage("Schedule status updated successfully");
        refetchScheduleList();
      })
      .catch((err: IHttpError) => {
        showErrorMessage(err?.message || "Failed to update schedule status");
      });
  };

  const handleDeleteSchedule = (id: string) => {
    deleteSchedule({ scheduleId: id })
      .unwrap()
      .then(() => {
        showSuccessMessage("Schedule deleted successfully");
        refetchScheduleList();
      })
      .catch((err: IHttpError) => {
        showErrorMessage(err?.message || "Failed to delete schedule");
      });
  };

  const handleRemoveDeviceConfirm = () => {
    removeEvCharger({ evChargerId: evChargerId || "" })
      .unwrap()
      .then(() => {
        showSuccessMessage("Device removed successfully");
        setShowRemoveDeviceModal(false);
        navigate(-1);
      })
      .catch((err: IHttpError) => {
        showErrorMessage(err?.message || "Failed to remove device");
      });
  };

  const SESSION_OPEN_STATUSES = [
    EEvChargerStatus.CHARGING,
    EEvChargerStatus.SUSPENDED_EV,
    EEvChargerStatus.SUSPENDED_EVSE,
  ];

  const TRANSIENT_STATUSES = [
    EEvChargerStatus.PREPARING,
    EEvChargerStatus.FINISHING,
  ];

  const UNCOMMANDABLE_STATUSES = [
    EEvChargerStatus.RESERVED,
    EEvChargerStatus.UNAVAILABLE,
    EEvChargerStatus.FAULTED,
    EEvChargerStatus.OFFLINE,
  ];

  const status = viewData?.evCharger?.status ?? null;
  const isSessionOpen =
    status !== null && SESSION_OPEN_STATUSES.includes(status);

  const isToggleDisabled =
    !viewData?.evCharger?.online ||
    isLoadingSessionStart ||
    isLoadingSessionStop ||
    (status !== null && TRANSIENT_STATUSES.includes(status)) ||
    (status !== null && UNCOMMANDABLE_STATUSES.includes(status)) ||
    status === null;

  const handleToggle = () => {
    if (!viewData?.evCharger?.id || isToggleDisabled) return;

    if (isSessionOpen) {
      chargingSessionStop({ evChargerId: viewData.evCharger.id })
        .unwrap()
        .then(() => {
          refetchEvCharger();
        })
        .catch((error: IHttpError) => {
          showErrorMessage(error.message || "Failed to stop charging session");
        });
    } else {
      chargingSessionStart({
        evChargerId: viewData.evCharger.id,
        ocppTagId: null,
        connectorId: null,
        vehicleProfileId: null,
        startingSocPercent: null,
      })
        .unwrap()
        .then(() => {
          refetchEvCharger();
        })
        .catch((error: IHttpError) => {
          showErrorMessage(error.message || "Failed to start charging session");
        });
    }
  };

  return (
    <div className="position-relative">
      <BackArrowButton onBack={() => navigate(-1)} />
      <Row className="g-4 mt-1 ui-stagger">
        <Col xs={12} xl={5} xxl={4}>
          <div
            className={`${styles.leftCard} h-100 bg-white p-4 d-flex flex-column gap-4`}
          >
            <Row className="align-items-center justify-content-between">
              <Col className="col-auto">
                <Row className="align-items-center g-3">
                  <Col className="col-auto">
                    <div className={`${styles.iconWrapper} p-3`}>
                      <MaterialIcon
                        icon="ev_station"
                        color="#788FFF"
                        size={28}
                      />
                    </div>
                  </Col>
                  <Col className="col-auto">
                    <Row>
                      <Col className="font-weight-500 font-size-16 text-dark">
                        {viewData?.evCharger?.name || "-"}
                      </Col>
                    </Row>
                    <Row className="row-cols-auto align-items-center g-1">
                      <Col>
                        <ActiveInactiveIndicator
                          isActive={viewData?.evCharger?.online || false}
                        />
                      </Col>
                      <Col className="col-auto text-light font-weight font-size-12">
                        {viewData?.evCharger?.powerUsageInWatt
                          ? `${Number(
                              Helper.roundTo2(
                                Number(viewData?.evCharger?.powerUsageInWatt),
                              ),
                            )}W`
                          : "-"}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col className="col-auto">
                <AppDropDown
                  bgColor="var(--sidebar-bg)"
                  items={[
                    {
                      text: "Edit Device",
                      onClick: () => setShowEditDeviceModal(true),
                    },
                    {
                      text: "Remove Device",
                      onClick: () => setShowRemoveDeviceModal(true),
                    },
                  ]}
                />
              </Col>
            </Row>
            <Row
              className={`${styles.infoGridBg} justify-content-between p-3 mx-0`}
            >
              <Col className="col-auto d-flex flex-column">
                <span className="font-size-14 text-dark font-weight-500">
                  Device Type
                </span>
                <span className="font-weight-400 font-size-14 text-light mt-1">
                  EV Charger
                </span>
              </Col>
              <Col className="col-auto d-flex flex-column">
                <span className="font-size-14 text-dark font-weight-500">
                  Brand
                </span>
                <span className="font-weight-400 font-size-14 text-light mt-1">
                  {viewData?.evCharger?.brand || "-"}
                </span>
              </Col>
              <Col className="col-auto d-flex flex-column">
                <span className="font-size-14 text-dark font-weight-500">
                  Model
                </span>
                <span className="font-weight-400 font-size-14 text-light mt-1">
                  {viewData?.evCharger?.modelNumber || "-"}
                </span>
              </Col>
            </Row>
            <EvChargerGauge isWavy={!!viewData?.evCharger?.currentPowerWatt}>
              <div className="d-flex flex-column align-items-center">
                {viewData?.evCharger.currentPowerWatt && (
                  <MaterialIcon
                    icon="flash_on"
                    color="#FFC72F"
                    size={22}
                    fill
                  />
                )}
                <span className="font-size-16 font-weight-500 text-light mt-1">
                  {status ? translate(status, "chargingStatus") : ""}&nbsp;
                  {viewData?.evCharger.socPercentage
                    ? `${viewData?.evCharger.socPercentage}%`
                    : ""}
                </span>
              </div>
              <div className="font-weight-500 font-size-32 text-dark m-0">
                {
                  Helper.formatPower(
                    Number(viewData?.evCharger?.currentPowerWatt ?? 0),
                  ).value
                }
                &nbsp;
                {
                  Helper.formatPower(
                    Number(viewData?.evCharger?.currentPowerWatt ?? 0),
                  ).unit
                }
              </div>
              <div className="mt-2">
                <AppToggle
                  isOn={isSessionOpen}
                  size="md"
                  disabled={isToggleDisabled}
                  onSwitch={handleToggle}
                />
              </div>
            </EvChargerGauge>
            <Row className="gx-3">
              <Col xs={6}>
                <button
                  type="button"
                  className={`${styles.hoverBorder} p-3 cursor-pointer h-100 w-100 text-start`}
                  style={{
                    backgroundColor:
                      selectedTab === "history"
                        ? "var(--background-4)"
                        : "var(--sidebar-bg)",
                  }}
                  aria-pressed={selectedTab === "history"}
                  onClick={() => setSelectedTab("history")}
                >
                  <Row className="align-items-center g-1">
                    <Col className="col-auto font-weight-600 font-size-12 text-dark">
                      Charge History
                    </Col>
                  </Row>
                  {lastChargePercent && (
                    <Row className="align-items-center g-1 mt-2">
                      <Col className="col-auto">
                        <MaterialIcon
                          icon="power"
                          size={16}
                          fill
                          color="#7483CC"
                        />
                      </Col>
                      <Col className="col-auto font-size-13 font-weight-600 text-dark ps-0">
                        {lastChargePercent}%
                      </Col>
                    </Row>
                  )}
                  <Row className="mt-1">
                    <Col className="font-size-12 text-light font-weight-400">
                      Last Charge
                    </Col>
                  </Row>
                  <Row>
                    <Col className="font-size-12 text-light font-weight-400">
                      ({lastChargeDate})
                    </Col>
                  </Row>
                </button>
              </Col>
              {/* <Col xs={6}>
                <div
                  className={`${styles.hoverBorder} p-3 cursor-pointer h-100`}
                  style={{
                    backgroundColor:
                      selectedTab === "schedule"
                        ? "var(--background-4)"
                        : "var(--sidebar-bg)",
                  }}
                  onClick={() => setSelectedTab("schedule")}
                >
                  <Row className="align-items-center g-1">
                    <Col className="col-auto">
                      <MaterialIcon icon="autoplay" color="#7483CC" size={20} />
                    </Col>
                    <Col className="col-auto font-weight-600 font-size-12 text-dark ps-2">
                      Schedule
                    </Col>
                    <Col className="col-auto">
                      <MaterialIcon
                        icon="chevron_right"
                        size={16}
                        color="#69768B"
                      />
                    </Col>
                  </Row>
                  <Row className="mt-1">
                    <Col className="font-size-12 text-light font-weight-400">
                      {scheduleCount > 0
                        ? `${scheduleCount} Schedule${
                            scheduleCount > 1 ? "s" : ""
                          } Available`
                        : "No Schedule Available"}
                    </Col>
                  </Row>
                </div>
              </Col> */}
            </Row>
          </div>
        </Col>
        <Col xs={12} xl={7} xxl={8}>
          {selectedTab === "history" && (
            <>
              <Row className="row-cols-2 row-cols-lg-3 row-cols-xl-3 g-3 mb-4">
                <Col>
                  <BillingSummaryCard
                    title="Total Usage"
                    value={Number(Helper.roundTo2(totalChartUsage))}
                    unit="kWh"
                    isLoading={isFetchingSessionList}
                  />
                </Col>
                <Col>
                  <BillingSummaryCard
                    title="Charge Sessions"
                    value={chargers}
                    isLoading={isFetchingSessionList}
                  />
                </Col>
                <Col>
                  <BillingSummaryCard
                    title="Users"
                    value={totalUsers}
                    isLoading={isFetchingSessionList}
                  />
                </Col>
              </Row>
              <div className={styles.chartCard}>
                <Row className="align-items-center g-3 mb-3">
                  <Col className="col-12 col-sm text-dark font-weight-500 font-size-16">
                    Charging History
                  </Col>
                  <Col className="col-12 col-sm-auto">
                    <Row className="align-items-center g-3">
                      <Col className="col-auto">
                        <div className="d-flex gap-2">
                          <MaterialIcon
                            icon="chevron_left"
                            size={18}
                            className="cursor-pointer text-light"
                            onClick={handlePrevChartDate}
                          />
                          <span className="font-size-14 text-light font-weight-400 text-nowrap">
                            {getGraphDateDisplay(
                              selectedChartDate,
                              chartViewType,
                            )}
                          </span>
                          <MaterialIcon
                            icon="chevron_right"
                            size={18}
                            className="cursor-pointer text-light"
                            onClick={handleNextChartDate}
                          />
                        </div>
                      </Col>
                      <Col className="col-auto">
                        <AppTabs
                          buttons={[
                            { id: 1, buttonName: "Day" },
                            { id: 2, buttonName: "Month" },
                            { id: 3, buttonName: "Year" },
                          ]}
                          onChange={setSelectedChartTabId}
                          selectedTabId={selectedChartTabId}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <BarChart
                  labels={chartLabels}
                  data1={chartData}
                  backgroundColor1="#7483CC"
                  borderColor1="#7483CC"
                  yAxesUnit="kWh"
                  xAxisTitle={getXAxisLabel(chartViewType)}
                  isChartDataAvailable={chartData.length > 0}
                  isDisplayLegend={false}
                  borderRadius={4}
                  borderSkipped={false}
                  barThickness={6}
                  displayGridLines={false}
                  width="100%"
                  height="300px"
                  minHeight="300px"
                />
              </div>
              <div className="text-dark font-weight-500 font-size-16 mt-4 mb-3">
                Charge History
              </div>
              {sessionListResponse?.sessions.elements &&
              sessionListResponse?.sessions.elements.length > 0 ? (
                <div>
                  {sessionListResponse.sessions.elements.map((item, index) => (
                    <ChargeHistoryCard
                      key={index}
                      dateRange={`${
                        item.startTime
                          ? moment(item.startTime).format("MMM DD HH:mm")
                          : ""
                      } - ${
                        item.stopTime
                          ? moment(item.stopTime).format("MMM DD HH:mm")
                          : ""
                      }`}
                      chargerName={item.chargeBoxName || ""}
                      userName={item.consumerName || ""}
                      id={`#${item.transactionId || ""}`}
                      usage={
                        item.energyConsumed != null
                          ? Number(Helper.roundTo2(Number(item.energyConsumed)))
                          : 0
                      }
                    />
                  ))}
                  <Pagination
                    length={sessionListResponse?.sessions.totalElements || 0}
                    itemsPerPage={historyItemsPerPage}
                    currentPage={historyCurrentPage}
                    updateCurrentPage={setHistoryCurrentPage}
                  />
                </div>
              ) : (
                <Row className="container-dash mx-0 mt-4">
                  <Col className="text-center font-weight-40 text-light">
                    There is no historical data
                  </Col>
                </Row>
              )}
            </>
          )}
          {selectedTab === "schedule" && (
            <>
              <Row className="align-items-center justify-content-between mt-3">
                <Col className="col-auto text-dark font-weight-600 font-size-20">
                  Schedule
                </Col>
                <Col className="col-auto">
                  <AppButton
                    text="Add Schedule"
                    size="medium"
                    onClick={() => setShowAddUpdateScheduleModal(true)}
                  />
                </Col>
              </Row>
              {scheduleListResponse?.schedules &&
              scheduleListResponse?.schedules.length > 0 ? (
                <Row>
                  <Col>
                    <DataTable
                      headers={[
                        {
                          id: 1,
                          columnName: "Name",
                          isSort: true,
                          sort: false,
                        },
                        {
                          id: 2,
                          columnName: "Time",
                          isSort: true,
                          sort: false,
                        },
                        {
                          id: 3,
                          columnName: "Type",
                          isSort: true,
                          sort: false,
                        },
                        {
                          id: 4,
                          columnName: "Status",
                          isSort: true,
                          sort: false,
                        },
                      ]}
                      data={(scheduleListResponse?.schedules || []).map(
                        (s: IChargingScheduleItem) => ({
                          data: [
                            <span
                              key="name"
                              className="font-weight-400 font-size-14 text-light"
                            >
                              {s.name || "-"}
                            </span>,
                            <span
                              key="time"
                              className="font-weight-400 font-size-14 text-light"
                            >
                              {s.scheduledPeriods &&
                              s.scheduledPeriods.length > 0
                                ? `${s.scheduledPeriods[0].from} - ${s.scheduledPeriods[0].to}`
                                : "-"}
                            </span>,
                            <span
                              key="type"
                              className="font-weight-400 font-size-14 text-light"
                            >
                              {s.scheduleType || "-"}
                            </span>,
                            <div
                              key="action"
                              className="d-flex align-items-center justify-content-between"
                            >
                              <AppToggle
                                isOn={s.status === "ACTIVE"}
                                size="sm"
                                variant="primary"
                                onSwitch={(val) =>
                                  handleToggleSchedule(s.scheduleId, val)
                                }
                              />
                              <AppDropDown
                                items={[
                                  {
                                    text: "Delete",
                                    onClick: () =>
                                      handleDeleteSchedule(s.scheduleId),
                                  },
                                ]}
                              />
                            </div>,
                          ],
                        }),
                      )}
                      className={styles.scheduleTable}
                    />
                  </Col>
                </Row>
              ) : (
                <Row className="container-dash mx-0 mt-4">
                  <Col className="text-center font-weight-40 text-light">
                    There is no schedule data
                  </Col>
                </Row>
              )}
            </>
          )}
        </Col>
      </Row>
      <AddUpdateScheduleModal
        show={showAddUpdateScheduleModal}
        onClose={() => setShowAddUpdateScheduleModal(false)}
        onCancel={() => setShowAddUpdateScheduleModal(false)}
        evChargerId={evChargerId || ""}
        onRefetch={() => {
          refetchScheduleList();
          setShowAddUpdateScheduleModal(false);
        }}
      />
      <AddUpdateDeviceModal
        show={showEditDeviceModal}
        deviceToEdit={viewData?.evCharger}
        onClose={() => setShowEditDeviceModal(false)}
        onCancel={() => setShowEditDeviceModal(false)}
        onSuccess={() => {
          refetchEvCharger();
          setShowEditDeviceModal(false);
        }}
      />
      <RemoveDeviceModal
        show={showRemoveDeviceModal}
        deviceName={viewData?.evCharger?.name ?? ""}
        onClose={() => setShowRemoveDeviceModal(false)}
        onCancel={() => setShowRemoveDeviceModal(false)}
        onConfirm={handleRemoveDeviceConfirm}
        isLoading={isRemovingEvCharger}
      />
      <SpinnerModal
        show={
          isFetchingEvCharger ||
          isFetchingSessionList ||
          isFetchingScheduleList ||
          isChangingScheduleState ||
          isDeletingSchedule ||
          isFetchingChartGraph
        }
      />
    </div>
  );
};

export default ViewDevice;
